import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Spinner } from '@optools/components/loading/spinner';
import { AlertDialogDescription } from '@optools/components/ui/alert-dialog';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { Switch } from '@optools/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { BtcGameAsset } from '@optools/services/operator-gateway/btc/types';
import {
  PipelineStatus,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';

import { canIngestAssetIntoDai } from '../utils/assets';

export enum AdStatus {
  ACTIVE = 'ACTIVE',
  ADS_PAUSED = 'ADS_PAUSED',
}

export type AdStatusSwitchProps = {
  asset: BtcGameAsset;
  isPending: boolean;
  onIngestIntoDai: (asset: BtcGameAsset) => void;
  onToggleAdStatus: (asset: BtcGameAsset, checked: boolean) => void;
  pipelineStatus: PipelineStatus;
  streamState: StreamState;
};

export function AdStatusSwitch({
  asset,
  isPending,
  onIngestIntoDai,
  onToggleAdStatus,
  pipelineStatus,
  streamState,
}: AdStatusSwitchProps) {
  const { adStatus } = asset;

  const handleToggleAdStatus = (checked: boolean) => {
    onToggleAdStatus(asset, checked);
  };

  const canIngestIntoDai = canIngestAssetIntoDai(pipelineStatus, streamState);

  if (!adStatus) {
    return canIngestIntoDai ? (
      <ConfirmationDialog
        description={
          <>
            <AlertDialogDescription>
              Are you sure you want to ingest this asset?
            </AlertDialogDescription>
            <div className="mt-4 w-full rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Tier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>{asset.assetId}</TableCell>
                    <TableCell>{asset.provider}</TableCell>
                    <TableCell>{asset.tier}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </>
        }
        title="Ingest Into DAI"
        onConfirm={() => onIngestIntoDai(asset)}>
        <Button disabled={isPending} loading={isPending} size="sm">
          Ingest Into DAI
        </Button>
      </ConfirmationDialog>
    ) : (
      <Badge variant="secondary">Not Supported</Badge>
    );
  }

  return (
    <div className="relative flex items-center justify-center gap-2 text-xs">
      {isPending && <Spinner className="absolute -left-6" variant="loader" />}
      <span>Paused</span>
      <Switch
        aria-label="ad status"
        checked={adStatus === AdStatus.ACTIVE}
        disabled={isPending}
        size="sm"
        onCheckedChange={handleToggleAdStatus}
      />
      <span>Active</span>
    </div>
  );
}
